/**
 * BidImportWizard — upload a lender bid sheet, review the reconciliation plan,
 * then apply it to the project. Two-step (analyze -> apply); nothing is written
 * until the user confirms. Gated behind the `bid_sheet_import` feature gate.
 */
import React, { useRef, useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  UploadCloud, Loader2, FileSpreadsheet, PlusCircle, RefreshCw,
  AlertTriangle, CheckCircle2, UserPlus, X,
} from 'lucide-react';
import { apiService } from '@/lib/api';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function BidImportWizard({ open, onOpenChange, projectId, userId, onApplied }) {
  const [step, setStep] = useState('upload'); // upload | analyzing | review | applying | done
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [clearActuals, setClearActuals] = useState(true);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setStep('upload'); setPlan(null); setRows([]); setResult(null); setClearActuals(true); };
  const close = (v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setStep('analyzing');
    try {
      const res = await apiService.bidImportAnalyze(projectId, userId, file);
      setPlan(res.data);
      setRows((res.data.line_items || []).map((li) => ({ ...li, include: true, _origPhase: li.construction_phase_number })));
      setStep('review');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that bid sheet.');
      setStep('upload');
    }
  };

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changePhase = (i, num) => {
    setRows((rs) => rs.map((r, idx) => {
      if (idx !== i) return r;
      const pn = num ? Number(num) : null;
      const phaseName = plan?.phases?.find((p) => p.number === pn)?.name || null;
      // if the phase no longer matches the originally-matched item, treat it as a new line-item
      const movedAway = r.action === 'update' && pn !== r._origPhase;
      return {
        ...r,
        construction_phase_number: pn,
        phase_name: phaseName,
        action: movedAway ? 'create' : r.action,
        existing_phase_item_id: movedAway ? null : r.existing_phase_item_id,
        will_clear_actual: movedAway ? false : r.will_clear_actual,
      };
    }));
  };

  // Live-recomputed summary from the editable rows (only included rows count).
  const s = useMemo(() => {
    const inc = rows.filter((r) => r.include);
    const subs = new Set(inc.filter((r) => r.contractor_is_new && r.subcontractor).map((r) => r.subcontractor.trim().toLowerCase()));
    const clearRows = inc.filter((r) => r.will_clear_actual);
    const bidByPhase = {};
    inc.forEach((r) => { if (r.construction_phase_number) bidByPhase[r.construction_phase_number] = (bidByPhase[r.construction_phase_number] || 0) + (Number(r.est_cost) || 0); });
    const before = plan?.summary?.budget_before_by_phase || {};
    const budget_flags = Object.entries(bidByPhase)
      .filter(([num, total]) => (before[num] || 0) > 0 && total > (before[num] || 0))
      .map(([num, total]) => ({
        phase_number: Number(num),
        phase_name: plan?.phases?.find((p) => p.number === Number(num))?.name,
        bid_total: total,
        budget_before: before[num],
        over_by: total - before[num],
      }));
    return {
      new_items: inc.filter((r) => r.action === 'create').length,
      updated_items: inc.filter((r) => r.action === 'update').length,
      excluded: rows.length - inc.length,
      contractors_new: subs.size,
      total_bid_cost: inc.reduce((a, r) => a + (Number(r.est_cost) || 0), 0),
      actuals_to_clear: clearRows.length,
      actuals_to_clear_total: clearRows.reduce((a, r) => a + (Number(r.existing_actual) || 0), 0),
      budget_flags,
    };
  }, [rows, plan]);

  const apply = async () => {
    setStep('applying');
    try {
      const payload = {
        user_id: userId,
        clear_actuals: clearActuals,
        create_contractors: true,
        line_items: rows.map((r) => ({
          category_name: r.category_name,
          subcontractor: r.subcontractor,
          est_cost: Number(r.est_cost) || 0,
          construction_phase_number: r.construction_phase_number,
          action: r.include ? r.action : 'skip',
          existing_phase_item_id: r.existing_phase_item_id,
        })),
      };
      const res = await apiService.bidImportApply(projectId, payload);
      setResult(res.data);
      setStep('done');
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed.');
      setStep('review');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="bid-import-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" /> Import Bid Sheet
          </DialogTitle>
          <DialogDescription>
            Upload your bid sheet — we&apos;ll match each line to a construction phase, set budgets, and assign subcontractors. Review everything before it&apos;s saved.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="py-8">
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.csv,.pdf" className="hidden" onChange={onFile} data-testid="bid-import-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="bid-import-upload-btn"
              className="w-full rounded-2xl border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40 transition px-6 py-12 flex flex-col items-center gap-3"
            >
              <UploadCloud className="w-10 h-10 text-emerald-500" />
              <span className="text-sm font-semibold text-slate-800">Choose a bid sheet to upload</span>
              <span className="text-xs text-slate-500">Excel (.xlsx / .xlsm / .xls), CSV, or PDF · up to 15MB</span>
            </button>
          </div>
        )}

        {step === 'analyzing' && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-600">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-sm font-semibold">Reading your bid sheet…</p>
            <p className="text-xs text-slate-400">Matching each line to a phase and finding subcontractors.</p>
          </div>
        )}

        {step === 'review' && plan && (
          <>
            <div className="flex flex-wrap gap-2 flex-shrink-0" data-testid="bid-import-summary">
              <Chip icon={PlusCircle} tone="emerald" label={`${s.new_items} new`} />
              <Chip icon={RefreshCw} tone="blue" label={`${s.updated_items} updated`} />
              {s.excluded > 0 && <Chip icon={X} tone="slate" label={`${s.excluded} excluded`} />}
              <Chip icon={UserPlus} tone="violet" label={`${s.contractors_new} new sub${s.contractors_new === 1 ? '' : 's'}`} />
              <Chip icon={FileSpreadsheet} tone="slate" label={`${money(s.total_bid_cost)} total`} />
            </div>

            {s.budget_flags?.length > 0 && (
              <div className="flex-shrink-0 mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2" data-testid="bid-import-budget-flags">
                <p className="text-xs font-bold text-red-700 flex items-center gap-1.5 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {s.budget_flags.length} phase{s.budget_flags.length === 1 ? '' : 's'} over prior budget
                </p>
                {s.budget_flags.map((f) => (
                  <p key={f.phase_number} className="text-[11px] text-red-700">
                    Phase {f.phase_number} · {f.phase_name}: bid {money(f.bid_total)} vs budget {money(f.budget_before)} — <b>over by {money(f.over_by)}</b>
                  </p>
                ))}
              </div>
            )}

            <p className="flex-shrink-0 mt-2 text-[11px] text-slate-400">Edit any cost, phase, or subcontractor below. Untick a row to leave it out of the import.</p>

            <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-1 border-t border-slate-100">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
                    <th className="py-2 pr-1 w-8"></th>
                    <th className="py-2 pr-2">Phase</th>
                    <th className="py-2 pr-2">Line item</th>
                    <th className="py-2 pr-2">Subcontractor</th>
                    <th className="py-2 pr-2 text-right">Bid cost</th>
                    <th className="py-2 pl-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((li, i) => (
                    <tr key={i} data-testid={`bid-import-row-${i}`} className={li.include ? '' : 'opacity-40'}>
                      <td className="py-2 pr-1 align-top">
                        <Checkbox checked={li.include} onCheckedChange={(v) => updateRow(i, { include: !!v })} className="mt-1.5" data-testid={`bid-import-include-${i}`} />
                      </td>
                      <td className="py-2 pr-2 align-top">
                        <select
                          value={li.construction_phase_number || ''}
                          onChange={(e) => changePhase(i, e.target.value)}
                          disabled={!li.include}
                          data-testid={`bid-import-phase-${i}`}
                          className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white max-w-[130px] disabled:bg-slate-50"
                        >
                          <option value="">— phase —</option>
                          {(plan.phases || []).map((p) => (
                            <option key={p.number} value={p.number}>P{p.number} · {p.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-2 align-top">
                        <input
                          value={li.category_name || ''}
                          onChange={(e) => updateRow(i, { category_name: e.target.value })}
                          disabled={!li.include}
                          data-testid={`bid-import-name-${i}`}
                          className="w-full min-w-[140px] text-sm border border-transparent hover:border-slate-200 focus:border-slate-300 rounded-md px-1.5 py-1 font-medium text-slate-800 disabled:bg-transparent"
                        />
                        {li.action === 'update' && li.existing_name && (
                          <span className="block text-[11px] text-slate-400 px-1.5">→ updates {li.existing_name}</span>
                        )}
                        {li.include && li.will_clear_actual && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 mt-0.5 px-1.5">
                            <AlertTriangle className="w-3 h-3" /> clears {money(li.existing_actual)} actual
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-2 align-top">
                        <input
                          value={li.subcontractor || ''}
                          onChange={(e) => updateRow(i, { subcontractor: e.target.value })}
                          disabled={!li.include}
                          placeholder="—"
                          data-testid={`bid-import-sub-${i}`}
                          className="w-full min-w-[110px] text-xs border border-transparent hover:border-slate-200 focus:border-slate-300 rounded-md px-1.5 py-1 text-slate-600 disabled:bg-transparent"
                        />
                        {li.contractor_is_new && li.subcontractor && <span className="text-[10px] text-violet-600 font-semibold px-1.5">NEW</span>}
                      </td>
                      <td className="py-2 pr-2 align-top text-right">
                        <div className="inline-flex items-center">
                          <span className="text-slate-400 text-xs">$</span>
                          <input
                            type="number"
                            value={li.est_cost}
                            onChange={(e) => updateRow(i, { est_cost: e.target.value })}
                            disabled={!li.include}
                            data-testid={`bid-import-cost-${i}`}
                            className="w-24 text-right text-sm border border-slate-200 rounded-md px-1.5 py-1 disabled:bg-slate-50"
                          />
                        </div>
                        {(plan.project_units || plan.project_sqft) ? (
                          <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`bid-import-permetrics-${i}`}>
                            {plan.project_units ? `${money((Number(li.est_cost) || 0) / plan.project_units)}/unit` : ''}
                            {plan.project_units && plan.project_sqft ? ' · ' : ''}
                            {plan.project_sqft ? `$${((Number(li.est_cost) || 0) / plan.project_sqft).toFixed(2)}/SF` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-2 pl-2 align-top">
                        {!li.include
                          ? <span className="text-[11px] font-semibold text-slate-400">Skipped</span>
                          : li.action === 'create'
                            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><PlusCircle className="w-3 h-3" /> New</span>
                            : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700"><RefreshCw className="w-3 h-3" /> Update</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex-shrink-0 border-t border-slate-100 pt-3 space-y-3">
              {s.actuals_to_clear > 0 && (
                <label className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 cursor-pointer" data-testid="bid-import-clear-actuals">
                  <Checkbox checked={clearActuals} onCheckedChange={(v) => setClearActuals(!!v)} className="mt-0.5" />
                  <span className="text-xs text-amber-800">
                    <b>Clear the {money(s.actuals_to_clear_total)} of actual costs</b> on {s.actuals_to_clear} matched item(s). These are estimates — actuals will be re-captured from receipts/payments as work happens. Uncheck to keep them.
                  </span>
                </label>
              )}
              <div className="flex items-center gap-2">
                <Button onClick={apply} disabled={s.new_items + s.updated_items === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bid-import-apply-btn">
                  Apply {s.new_items + s.updated_items} item{s.new_items + s.updated_items === 1 ? '' : 's'}
                </Button>
                <Button variant="ghost" onClick={() => close(false)} data-testid="bid-import-cancel-btn">Cancel</Button>
              </div>
            </div>
          </>
        )}

        {step === 'applying' && (
          <div className="py-16 flex flex-col items-center gap-3 text-slate-600">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-sm font-semibold">Applying the bid sheet to your project…</p>
          </div>
        )}

        {step === 'done' && result && (
          <div className="py-10 flex flex-col items-center gap-3 text-center" data-testid="bid-import-done">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
            <p className="text-base font-bold text-slate-900">Bid sheet imported</p>
            <p className="text-sm text-slate-600">
              {result.created_items} new line item(s), {result.updated_items} updated, {result.contractors_created} subcontractor(s) created.
              {result.actuals_cleared ? ' Actual costs were cleared.' : ''}
            </p>
            <Button onClick={() => close(false)} className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="bid-import-close-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TONES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
};
const Chip = ({ icon: Icon, tone, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-2.5 py-1 ${TONES[tone]}`}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </span>
);
